// MagicLinkLoginPage — branded email form for magic link authentication.
// Rendered before authentication is established (no ApiClient available).
// Applies tenant branding via the unauthenticated branding hook.
//
// Possible states:
//   1. idle        — email form
//   2. submitting  — spinner while POST is in flight
//   3. sent        — "check your email" confirmation
//   4. error       — generic error (never reveals whether email exists)
//
// The URL may include ?error=expired | invalid | used | server from the
// backend redirect on failed callback — show the appropriate hint.

import { useState, type FormEvent } from 'react'
import { usePublicBranding } from '@/lib/hooks/use-public-branding'

interface MagicLinkLoginPageProps {
  slug: string
  /** Error param from the callback redirect (?error=...) */
  initialError?: string | null
}

type PageState = 'idle' | 'submitting' | 'sent' | 'error'

function resolveInitialError(error: string | null | undefined): string | null {
  if (!error) return null
  switch (error) {
    case 'expired':
      return 'Your login link has expired. Enter your email to request a new one.'
    case 'used':
      return 'This login link has already been used. Enter your email to request a new one.'
    case 'invalid':
      return 'This login link is invalid. Enter your email to request a new one.'
    case 'server':
      return 'Something went wrong. Please try again.'
    default:
      return 'Your session has expired. Enter your email to continue.'
  }
}

export function MagicLinkLoginPage({ slug, initialError }: MagicLinkLoginPageProps) {
  const { logoUrl } = usePublicBranding(slug)
  const [email, setEmail] = useState('')
  const [pageState, setPageState] = useState<PageState>('idle')
  const [errorHint] = useState<string | null>(() => resolveInitialError(initialError))

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()

    const trimmed = email.trim()
    if (!trimmed) return

    setPageState('submitting')

    try {
      await fetch(`/t/${encodeURIComponent(slug)}/auth/magic-link-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      })
      // Always show "sent" — backend never reveals whether the email exists.
      setPageState('sent')
    } catch {
      setPageState('error')
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm">
        {/* Logo / header */}
        <div className="mb-8 text-center">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="Organisation logo"
              className="mx-auto mb-4 h-10 max-w-[160px] object-contain"
            />
          ) : (
            <div className="mx-auto mb-4 flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <span className="text-sm font-bold">RQ</span>
            </div>
          )}
          <h1 className="text-xl font-semibold text-foreground">Sign in to RiskQuilt</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Enter your email and we&apos;ll send you a login link.
          </p>
        </div>

        {/* Session-expired / error hint from callback redirect */}
        {errorHint && pageState === 'idle' && (
          <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-sm text-amber-800">{errorHint}</p>
          </div>
        )}

        {pageState === 'sent' ? (
          /* Confirmation state */
          <div className="rounded-lg border border-border bg-card p-6 text-center shadow-sm">
            <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
              {/* Envelope icon */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-primary"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
              </svg>
            </div>
            <h2 className="font-semibold text-foreground">Check your email</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              If an account exists for <strong>{email}</strong>, a login link has been sent.
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              The link expires in 1 hour and can only be used once.
            </p>
            <button
              type="button"
              onClick={() => {
                setEmail('')
                setPageState('idle')
              }}
              className="mt-4 text-sm text-primary underline-offset-4 hover:underline"
            >
              Use a different email address
            </button>
          </div>
        ) : (
          /* Email form */
          <form onSubmit={handleSubmit} className="rounded-lg border border-border bg-card p-6 shadow-sm">
            {pageState === 'error' && (
              <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3">
                <p className="text-sm text-destructive">
                  Something went wrong. Please try again.
                </p>
              </div>
            )}

            <div className="mb-4">
              <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground">
                Email address
              </label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@organisation.com"
                disabled={pageState === 'submitting'}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 disabled:opacity-50"
              />
            </div>

            <button
              type="submit"
              disabled={pageState === 'submitting' || !email.trim()}
              className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {pageState === 'submitting' ? (
                <span className="flex items-center justify-center gap-2">
                  <svg
                    className="h-4 w-4 animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Sending…
                </span>
              ) : (
                'Send login link'
              )}
            </button>
          </form>
        )}

        <p className="mt-6 text-center text-xs text-muted-foreground">
          Powered by{' '}
          <span className="font-medium text-foreground">RiskQuilt</span>
        </p>
      </div>
    </div>
  )
}
